import React, { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { number, object } from 'yup';

import { getTypedFormComponents } from 'Src/feature/form/getTypedFormComponents';
import { useForm } from 'Src/feature/form/useForm';
import { Modal } from 'Src/feature/modal/Modal';
import { ModalAction } from 'Src/feature/modal/ModalAction';
import { ModalActions } from 'Src/feature/modal/ModalActions';
import { ModalContent } from 'Src/feature/modal/ModalContent';
import { ModalHeader } from 'Src/feature/modal/ModalHeader';
import { ModalSubTitle } from 'Src/feature/modal/ModalSubTitle';
import { ModalTitle } from 'Src/feature/modal/ModalTitle';
import { useCheckPagePayment } from 'Src/feature/payment/useCheckPagePayment';

import styles from './PartialPaymentModal.module.scss';

const TEST_ID = {
  root: `PartialPaymentModal`,
  cta: {
    cancel: `PartialPaymentModal-cancel`,
    next: `PartialPaymentModal-next`,
  }
};

type FormModel = {
 total: number;
}

const { Form, Field } = getTypedFormComponents<FormModel>();

type PartialPaymentModalControl = ReturnType<typeof useCheckPagePayment>['modal']['part'];

interface PartialPaymentModalProps {
  control: PartialPaymentModalControl;
}

const PartialPaymentModalContent: FC<PartialPaymentModalProps> = ({ control }) => {
  const { t } = useTranslation();
  const messageContainerRef = useRef<HTMLDivElement>(null);

  const form = useForm<FormModel>({
    defaultValues: { total: undefined },
    schema: object().shape({
      // balance should never be undefined, but default to 1000 just in case and to make typescript happy
      total: number().label(t('labels.amount'))
        .required()
        .moreThan(0).max(control.check?.balance.unpaid ?? 1000),
    }),
  });
  
  const customAmount = form.state.watch('total');

  return (
    <>
      <ModalHeader>
        <ModalTitle copy={t('payment.labels.partialPayment')} />
        <ModalSubTitle copy={t('payment.labels.partialPaymentInstructions')} />
      </ModalHeader>
      <ModalContent flex={{ alignItems: 'center', justifyContent: 'center' }}>
        <Form usedForm={form} omitChangeTracking className={styles.form}>
          <Field
            type="currencyInput"
            name="total"
            className={styles.field}
            subtitle={t('payment.labels.amountPaidVsDue', {
              paid: control.check?.balance.paid ?? 0,
              due: control.check?.balance.unpaid ?? 0
            })}
            messageContainer={messageContainerRef.current ?? undefined}
          />
        </Form>
        <div ref={messageContainerRef} />
      </ModalContent>
      <ModalActions>
        <ModalAction
          data-testid={TEST_ID.cta.cancel}
          color="secondary"
          label={t('labels.cancel')}
          onClick={() => control.close('on-user-cancel')}
        />
        <ModalAction
          data-testid={TEST_ID.cta.next}
          color="primary"
          label={t('labels.next')}
          onClick={() => control.onNext(parseFloat(customAmount?.toString() ?? '0'))}
          disabled={!form.state.isValid}
        />
      </ModalActions>
    </>
  );
};

// eslint-disable-next-line react/no-multi-comp
const PartialPaymentModal: FC<PartialPaymentModalProps> = ({ control }) => (
  <Modal
    data-testid={TEST_ID.root}
    open={control.isOpen}
    onBackDropClick={() => control.close('on-user-cancel')}
  >
    <PartialPaymentModalContent control={control} />
  </Modal>
);

export { PartialPaymentModal, TEST_ID };
export type { PartialPaymentModalProps };
